<template>
  <section class="first-free-stream-banner" role="status" aria-live="polite">
    <div class="first-free-stream-banner__border" aria-hidden="true"></div>
    <div class="first-free-stream-banner__glow first-free-stream-banner__glow--left" aria-hidden="true"></div>
    <div class="first-free-stream-banner__glow first-free-stream-banner__glow--right" aria-hidden="true"></div>
    <div class="first-free-stream-banner__sparkles" aria-hidden="true">
      <span v-for="n in 6" :key="n" class="first-free-stream-banner__sparkle"></span>
    </div>

    <div class="first-free-stream-banner__inner">
      <div class="first-free-stream-banner__hero">
        <div class="first-free-stream-banner__icon-wrap">
          <span class="first-free-stream-banner__icon-ring"></span>
          <span class="first-free-stream-banner__icon-ring first-free-stream-banner__icon-ring--delay"></span>
          <svg class="first-free-stream-banner__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8M12 22V12M12 12H4.5a2.5 2.5 0 0 1 0-5C7 7 8 4 12 4s5 3 6.5 3a2.5 2.5 0 0 1 0 5H12Z"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path d="M12 12V7.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </div>

        <div class="first-free-stream-banner__copy">
          <span class="first-free-stream-banner__chip">
            <span class="first-free-stream-banner__chip-dot"></span>
            {{ tt("makeClips.firstFreeStreamBannerChip", "Oferta de bienvenida") }}
          </span>
          <p class="first-free-stream-banner__title mb-0">
            {{ tt("makeClips.firstFreeStreamBannerTitle", "Tu primer stream gratis de hasta 4 horas") }}
          </p>
          <p class="first-free-stream-banner__subtitle mb-0">
            {{ tt("makeClips.firstFreeStreamBannerSubtitle", "Aprovecha esta ventana para crear tu primer stream sin costo.") }}
          </p>
        </div>
      </div>

      <div class="first-free-stream-banner__countdown-box">
        <span class="first-free-stream-banner__label">
          {{ tt("makeClips.firstFreeStreamCountdownLabel", "Aprovecha la oferta, tiempo restante") }}
        </span>
        <strong class="first-free-stream-banner__countdown">{{ countdownText }}</strong>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "FirstFreeStreamBanner",
  props: {
    countdownText: {
      type: String,
      required: true,
    },
  },
  methods: {
    tt(key, fallback, params) {
      return typeof this.$t === "function" ? this.$t(key, params) : fallback;
    },
  },
};
</script>

<style scoped>
.first-free-stream-banner {
  --banner-accent: #a855f7;
  --banner-accent-hot: #d946ef;
  --banner-cyan: #22d3ee;
  --banner-gold: #fbbf24;
  position: relative;
  width: min(1100px, 100%);
  margin: 0 auto 18px;
  padding: 2px;
  border-radius: 20px;
  overflow: hidden;
  isolation: isolate;
}

.first-free-stream-banner__border {
  position: absolute;
  inset: -50%;
  background: conic-gradient(
    from 0deg,
    var(--banner-accent-hot),
    var(--banner-cyan),
    var(--banner-gold),
    var(--banner-accent),
    var(--banner-accent-hot)
  );
  animation: banner-border-spin 4s linear infinite;
  z-index: 0;
}

.first-free-stream-banner__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  pointer-events: none;
  z-index: 1;
}

.first-free-stream-banner__glow--left {
  width: 220px;
  height: 220px;
  top: -80px;
  left: -40px;
  background: rgba(217, 70, 239, 0.45);
  animation: banner-glow-pulse 3s ease-in-out infinite;
}

.first-free-stream-banner__glow--right {
  width: 180px;
  height: 180px;
  bottom: -70px;
  right: 80px;
  background: rgba(34, 211, 238, 0.35);
  animation: banner-glow-pulse 3s ease-in-out infinite 1.2s;
}

.first-free-stream-banner__sparkles {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
}

.first-free-stream-banner__sparkle {
  position: absolute;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.9);
  animation: banner-sparkle 2.4s ease-in-out infinite;
}

.first-free-stream-banner__sparkle:nth-child(1) { top: 18%; left: 8%; animation-delay: 0s; }
.first-free-stream-banner__sparkle:nth-child(2) { top: 72%; left: 22%; animation-delay: 0.4s; }
.first-free-stream-banner__sparkle:nth-child(3) { top: 28%; left: 58%; animation-delay: 0.8s; }
.first-free-stream-banner__sparkle:nth-child(4) { top: 62%; left: 78%; animation-delay: 1.2s; }
.first-free-stream-banner__sparkle:nth-child(5) { top: 12%; left: 88%; animation-delay: 1.6s; }
.first-free-stream-banner__sparkle:nth-child(6) { top: 84%; left: 48%; animation-delay: 2s; }

.first-free-stream-banner__inner {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 22px;
  border-radius: 18px;
  background:
    radial-gradient(circle at 12% 50%, rgba(168, 85, 247, 0.22), transparent 42%),
    radial-gradient(circle at 88% 50%, rgba(34, 211, 238, 0.14), transparent 38%),
    linear-gradient(135deg, rgba(15, 10, 30, 0.97) 0%, rgba(17, 24, 39, 0.96) 55%, rgba(12, 18, 34, 0.98) 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 20px 48px rgba(8, 6, 20, 0.55);
}

.first-free-stream-banner__hero {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.first-free-stream-banner__icon-wrap {
  position: relative;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(217, 70, 239, 0.35), rgba(34, 211, 238, 0.2));
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow:
    0 0 24px rgba(168, 85, 247, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.first-free-stream-banner__icon-ring {
  position: absolute;
  inset: -6px;
  border-radius: 20px;
  border: 2px solid rgba(251, 191, 36, 0.55);
  animation: banner-ring-pulse 2s ease-out infinite;
}

.first-free-stream-banner__icon-ring--delay {
  animation-delay: 1s;
}

.first-free-stream-banner__icon {
  width: 28px;
  height: 28px;
  color: #fde68a;
  filter: drop-shadow(0 0 6px rgba(251, 191, 36, 0.65));
}

.first-free-stream-banner__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.first-free-stream-banner__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fef3c7;
  background: linear-gradient(90deg, rgba(251, 191, 36, 0.28), rgba(217, 70, 239, 0.22));
  border: 1px solid rgba(251, 191, 36, 0.45);
  box-shadow: 0 0 16px rgba(251, 191, 36, 0.25);
}

.first-free-stream-banner__chip-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fbbf24;
  box-shadow: 0 0 8px #fbbf24;
  animation: banner-dot-blink 1.4s ease-in-out infinite;
}

.first-free-stream-banner__title {
  font-size: clamp(1.05rem, 2vw, 1.28rem);
  font-weight: 800;
  line-height: 1.25;
  background: linear-gradient(90deg, #ffffff 0%, #e9d5ff 45%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: none;
  filter: drop-shadow(0 0 18px rgba(168, 85, 247, 0.35));
}

.first-free-stream-banner__subtitle {
  font-size: 0.88rem;
  line-height: 1.45;
  color: rgba(226, 232, 240, 0.82);
}

.first-free-stream-banner__countdown-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 200px;
  max-width: 240px;
  padding: 10px 14px;
  border-radius: 14px;
  background: rgba(2, 6, 23, 0.55);
  border: 1px solid rgba(168, 85, 247, 0.45);
  box-shadow:
    inset 0 0 20px rgba(168, 85, 247, 0.12),
    0 0 24px rgba(168, 85, 247, 0.2);
  flex-shrink: 0;
}

.first-free-stream-banner__label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-align: center;
  line-height: 1.35;
  color: rgba(216, 180, 254, 0.95);
}

.first-free-stream-banner__countdown {
  font-size: 1.35rem;
  line-height: 1;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.06em;
  background: linear-gradient(180deg, #ffffff 0%, #c4b5fd 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 0 10px rgba(196, 181, 253, 0.5));
}

@keyframes banner-border-spin {
  to { transform: rotate(360deg); }
}

@keyframes banner-glow-pulse {
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 0.9; transform: scale(1.08); }
}

@keyframes banner-sparkle {
  0%, 100% { opacity: 0; transform: scale(0.4); }
  50% { opacity: 1; transform: scale(1); }
}

@keyframes banner-ring-pulse {
  0% { opacity: 0.8; transform: scale(0.92); }
  100% { opacity: 0; transform: scale(1.35); }
}

@keyframes banner-dot-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

@media (max-width: 900px) {
  .first-free-stream-banner__inner {
    flex-direction: column;
    align-items: stretch;
    padding: 16px 18px;
  }

  .first-free-stream-banner__countdown-box {
    width: 100%;
    max-width: none;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .first-free-stream-banner__border,
  .first-free-stream-banner__glow,
  .first-free-stream-banner__sparkle,
  .first-free-stream-banner__icon-ring,
  .first-free-stream-banner__chip-dot {
    animation: none;
  }
}
</style>
